import { Modal, Tab } from "bootstrap";
import { LoadingButton } from "../loadingButton";
import { showReturnNotification } from "./dashboard_utils";
import { TimeoutError } from "../utils";
import { waitForTurnstilePromiseOrTimeout } from "../turnstile";
// Domain management functionality
export function setupDomainVerification() {
    // Set up verification method selection
    setupVerificationMethodSelection();
    // Set up domain verification button
    setupDomainVerificationModal();
    // Add global modal cleanup handler
    // TODO: Need to investigate why this is needed
    setupModalCleanup();
}
function setupVerificationMethodSelection() {
    // With Bootstrap's tab component, we don't need to manually handle tab switching
    // as it's handled by Bootstrap's JavaScript.
    // We just need to initialize the tabs if needed.
    // Store the current verification method when a tab is clicked
    const tabButtons = document.queryHtmlElements("#verificationTabs .nav-link");
    for (const tab of tabButtons) {
        tab.addEventListener("shown.bs.tab", (event) => {
            if (event.target && event.target instanceof HTMLElement) {
                // Get the ID of the activated tab (without the -tab suffix)
                const activeMethod = event.target.id.replace("-tab", "");
                // Store this as the current method - useful when verifying
                localStorage.setItem("current_verification_method", activeMethod);
                // If we're on the email tab hide the verify button and show the continue button
                if (activeMethod === "email") {
                    document.whenHtmlElementExists("verifyButton", (verifyButton) => {
                        verifyButton.bootstrapHide();
                    });
                    document.whenHtmlElementExists("continueToDomainVerification", (continueButton) => {
                        continueButton.bootstrapShow();
                    });
                }
                else {
                    document.whenHtmlElementExists("verifyButton", (verifyButton) => {
                        verifyButton.bootstrapShow();
                    });
                    document.whenHtmlElementExists("continueToDomainVerification", (continueButton) => {
                        continueButton.bootstrapHide();
                    });
                }
            }
        });
    }
    // Initialize with DNS as the default method
    localStorage.setItem("current_verification_method", "email");
}
function setupDomainVerificationModal() {
    // Get elements for step 1
    const domainInputStep = document.getHtmlElementById("domainInputStep");
    const verificationMethodsStep = document.getHtmlElementById("verificationMethodsStep");
    const emailTokenStep = document.getHtmlElementById("emailTokenStep");
    const continueButton = document.getHtmlElementById("continueToDomainVerification");
    const domainInput = document.getHtmlElementById("domainInput");
    const emailTokenInput = document.getHtmlElementById("emailToken");
    const goBackButton = document.getHtmlElementById("goBackButton");
    // Get elements for step 2
    const verifyButton = document.getHtmlElementById("verifyButton");
    const tabContent = document.getHtmlElementById("verificationTabContent");
    // When we show the modal, let's make sure the domainInput has focus
    document.whenHtmlElementExists("verificationModal", (modalElement) => {
        modalElement.addEventListener("shown.bs.modal", () => {
            if (domainInput) {
                domainInput.focus();
            }
        });
    });
    // When we press enter in the domainInput, we want to trigger the continue button
    if (domainInput) {
        domainInput.addEventListener("keypress", (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                if (continueButton) {
                    continueButton.click();
                }
            }
        });
    }
    if (emailTokenInput) {
        emailTokenInput.addEventListener("keypress", (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                if (verifyButton) {
                    verifyButton.click();
                }
            }
        });
    }
    // Handle the continue button to move from step 1 to loading to step 2
    if (continueButton && domainInput && emailTokenInput) {
        const continueLoadingButton = LoadingButton.getOrCreateInstance(continueButton);
        continueButton.addEventListener("click", async () => {
            try {
                // If we are currently in the verification methods step and email has been selected
                if (verificationMethodsStep?.isBootstrapVisible() &&
                    localStorage.getItem("current_verification_method") === "email" &&
                    emailTokenStep &&
                    continueButton &&
                    verifyButton) {
                    // Let's wait for either the turnstile promise to resolve or a timeout of 10 seconds
                    await waitForTurnstilePromiseOrTimeout(10000);
                    // Submit the domain verification form with the "email" method to trigger sending the verificationEmail before we show the email token input
                    const { result, verificationResultObject } = await submitDomainVerification("email");
                    if (result.ok && verificationResultObject.verificationResult === "VerificationEmailSent") {
                        // Hide step 2, show email token input step
                        verificationMethodsStep.bootstrapHide();
                        emailTokenStep.bootstrapShow();
                        // hide the continue button and show the verify button
                        continueButton.bootstrapHide();
                        verifyButton.bootstrapShow();
                    }
                    else {
                        // Show an error message if the email verification failed
                        let errorString = "";
                        if ("TurnstileValidation" in verificationResultObject) {
                            errorString =
                                "Our anti-automation thinks you're a bot, which you're obviously not! Try behaving like a human and clicking the button again and if it still misbehaves, give the page a reload.";
                        }
                        else {
                            errorString = verificationResultObject.verificationDescription;
                        }
                        showReturnNotification("danger", errorString);
                        hideVerificationModal();
                    }
                }
                else {
                    await gotoDomainVerificationStep2();
                }
            }
            catch (error) {
                if (error instanceof TimeoutError) {
                    showReturnNotification("danger", "Our anti-automation seems to have timed out. Try again or reload the page. If the issue persists, try retrying the operation through an incognito/in-private browser window.");
                }
                else {
                    showReturnNotification("danger", `Error: ${error instanceof Error ? error.message : "Unknown error"}`);
                }
            }
            finally {
                continueLoadingButton.stop();
            }
        });
        // Clear invalid state when input changes
        domainInput.addEventListener("input", function () {
            this.classList.remove("is-invalid");
        });
    }
    // Handle go back button to return to step 1 or close modal depending on current step
    if (goBackButton) {
        goBackButton.addEventListener("click", (event) => {
            // If we're in step 2, go back to step 1 and prevent modal from closing
            if (verificationMethodsStep?.isBootstrapVisible() && domainInputStep && verifyButton && continueButton) {
                // Prevent the default dismiss behavior
                event.preventDefault();
                // Go back to step 1
                domainInputStep.bootstrapShow();
                verificationMethodsStep.bootstrapHide();
                verifyButton.bootstrapHide(); // Hide the verify button
                continueButton.bootstrapShow(); // Show continue button
                // Hide the Go Back button when back in step 1
                goBackButton.bootstrapHide();
                // Restore the data-bs-dismiss attribute for step 1
                goBackButton.dataset.bsDismiss = "modal";
            }
        });
    }
    // Handle the verify button functionality
    if (verifyButton && domainInput) {
        const verifyLoadingButton = LoadingButton.getOrCreateInstance(verifyButton);
        verifyButton.addEventListener("click", async () => {
            try {
                const domain = domainInput.value.trim();
                if (!domain) {
                    // We shouldn't reach here, but just in case
                    if (domainInputStep) {
                        domainInputStep.bootstrapShow();
                    }
                    if (verificationMethodsStep) {
                        verificationMethodsStep.bootstrapHide();
                    }
                    domainInput.classList.add("is-invalid");
                    return;
                }
                const verificationMethod = localStorage.getItem("current_verification_method");
                if (verificationMethod) {
                    // Hide tab content
                    if (tabContent) {
                        tabContent.bootstrapHide();
                    }
                    // Send the verification request but let's check if we're validating the email code and then send emaiToken as verificationMethod
                    const { result, verificationResultObject } = await submitDomainVerification(emailTokenStep?.classList.contains("d-none") === false ? "emailToken" : verificationMethod);
                    // Hide modal
                    hideVerificationModal();
                    if (result.status === 200) {
                        // Let's reload the page
                        window.location.reload();
                    }
                    else {
                        showReturnNotification("danger", verificationResultObject.verificationDescription);
                    }
                }
            }
            finally {
                verifyLoadingButton.stop();
            }
        });
    }
    // Hook up the verification tool buttons
    // Enable the verification tools button
    const buttons = document.queryHtmlElements(".verification-button");
    for (const verificationButton of buttons) {
        verificationButton.addEventListener("click", async () => {
            const steps = document.queryHtmlElements(".domain-step");
            for (const step of steps) {
                step.bootstrapHide();
            }
            const domainInput = document.getHtmlElementById("domainInput");
            if (domainInput) {
                domainInput.value = verificationButton.dataset.domain ?? "";
                await gotoDomainVerificationStep2();
                showVerificationModal();
            }
        });
    }
    async function submitDomainVerification(verificationMethod) {
        const formElement = document.getHtmlElementById("addDomainForm");
        if (formElement && verificationMethod) {
            const formData = new FormData(formElement);
            const data = new URLSearchParams();
            formData.forEach((value, key) => {
                data.append(key, value.toString());
            });
            const result = await fetch(`/Dashboard/SubmitDomainVerification?verificationMode=${verificationMethod}`, {
                body: data,
                method: "POST",
            });
            const verificationResultObject = await result.json();
            return { result, verificationResultObject };
        }
        throw new Error("Form element not found or verification method is invalid.");
    }
    async function gotoDomainVerificationStep2() {
        const domainInput = document.getHtmlElementById("domainInput");
        const websiteToken = document.getHtmlElementById("websiteToken");
        const continueButton = document.getHtmlElementById("continueToDomainVerification");
        if (domainInput?.value && websiteToken && continueButton) {
            const continueLoadingButton = LoadingButton.getOrCreateInstance(continueButton);
            try {
                const domain = domainInput.value.trim();
                if (!domain) {
                    domainInput.classList.add("is-invalid");
                    return;
                }
                const verificationTokenResults = await fetch(`/Dashboard/GenerateDomainVerificationToken?domainName=${encodeURIComponent(domainInput.value)}`);
                if (verificationTokenResults.status !== 200) {
                    showReturnNotification("danger", await verificationTokenResults.text());
                    hideVerificationModal();
                    return;
                }
                const verificationTokenData = (await verificationTokenResults.json());
                if (verificationTokenData.alreadyVerified) {
                    const { result, verificationResultObject } = await submitDomainVerification("alreadyVerified");
                    if (result.status === 200 && verificationResultObject.verificationResult === "VerificationSuccessful") {
                        window.location.reload();
                    }
                    else {
                        showReturnNotification("danger", verificationResultObject.verificationDescription);
                        hideVerificationModal();
                    }
                }
                // Remove the data-bs-dismiss attribute from the goBackButton
                // as we don't want it to close the modal in step 2
                if (goBackButton) {
                    goBackButton.removeAttribute("data-bs-dismiss");
                }
                // Hide step 1, show loading step
                if (domainInputStep) {
                    domainInputStep.bootstrapHide();
                }
                websiteToken.value = verificationTokenData.websiteToken;
                // Setup the DNS instructions
                document.whenHtmlElementExists("dnsInstructionsValue", (dnsInstructionsValue) => {
                    dnsInstructionsValue.innerHTML = `hibp-verify=${verificationTokenData.websiteToken}`;
                });
                // Show the File Instructions
                document.whenHtmlElementExists("fileInstructionsValue", "fileInstructionsDetails", (fileInstructionsValue, fileInstructionsDetails) => {
                    fileInstructionsValue.innerHTML = `${verificationTokenData.websiteToken}`;
                    fileInstructionsDetails.innerHTML = `The file should be accessible at: <code>https://${domainInput.value}/hibp-verification.txt</code>`;
                });
                // Show the Meta Instructions
                document.whenHtmlElementExists("metaInstructionsValue", (metaInstructionsValue) => {
                    metaInstructionsValue.innerText = `<meta name="hibp-verify" content="${verificationTokenData.websiteToken}" />`;
                });
                const radioButtons = document.queryHtmlElements(".form-check-input[type='radio']");
                for (const radioButton of radioButtons) {
                    radioButton.value = `${radioButton.dataset.alias ?? ""}@${domainInput.value}`;
                }
                document.whenHtmlElementExists("emailAdmin", "emailWebmaster", "emailInfo", "emailHostmaster", "emailSecurity", (emailAdminElement, emailWebmasterElement, emailInfoElement, emailHostmasterElement, emailSecurityElement) => {
                    emailAdminElement.innerText = `admin@${domainInput.value}`;
                    emailWebmasterElement.innerText = `webmaster@${domainInput.value}`;
                    emailInfoElement.innerText = `info@${domainInput.value}`;
                    emailHostmasterElement.innerText = `hostmaster@${domainInput.value}`;
                    emailSecurityElement.innerText = `security@${domainInput.value}`;
                });
                const domainNamePlaceholders = document.queryHtmlElements(".domain-name-placeholder");
                for (const placeholder of domainNamePlaceholders) {
                    placeholder.innerText = domainInput.value;
                }
                // If we're on the email tab hide the verify button and show the continue button
                if (localStorage.getItem("current_verification_method") === "email") {
                    document.whenHtmlElementExists("verifyButton", (verifyButton) => {
                        verifyButton.bootstrapHide();
                    });
                    document.whenHtmlElementExists("continueToDomainVerification", (continueButton) => {
                        continueButton.bootstrapShow();
                    });
                }
                else {
                    document.whenHtmlElementExists("verifyButton", (verifyButton) => {
                        verifyButton.bootstrapShow();
                    });
                    document.whenHtmlElementExists("continueToDomainVerification", (continueButton) => {
                        continueButton.bootstrapHide();
                    });
                }
                // Hide loading step, show step 2
                if (verificationMethodsStep) {
                    verificationMethodsStep.bootstrapShow();
                }
                // Show the Go Back button when in step 2
                if (goBackButton) {
                    goBackButton.bootstrapShow();
                }
            }
            finally {
                continueLoadingButton.stop();
            }
        }
    }
}
function hideVerificationModal() {
    document.whenHtmlElementExists("verificationModal", (modalElement) => {
        Modal.getOrCreateInstance(modalElement).hide();
    });
}
function showVerificationModal() {
    document.whenHtmlElementExists("verificationModal", (modalElement) => {
        Modal.getOrCreateInstance(modalElement).show();
    });
}
function setupModalCleanup() {
    document.whenHtmlElementExists("verificationModal", (verificationModal) => {
        verificationModal.addEventListener("hidden.bs.modal", () => {
            // Reset modal to step 1 when closed
            const domainInputStep = document.getRequiredHtmlElementById("domainInputStep");
            const verificationMethodsStep = document.getRequiredHtmlElementById("verificationMethodsStep");
            const verifyButton = document.getRequiredHtmlElementById("verifyButton");
            const continueButton = document.getRequiredHtmlElementById("continueToDomainVerification");
            const domainInput = document.getRequiredHtmlElementById("domainInput");
            const tabContent = document.getRequiredHtmlElementById("verificationTabContent");
            const goBackButton = document.getRequiredHtmlElementById("goBackButton");
            const emailTokenStep = document.getRequiredHtmlElementById("emailTokenStep");
            const emailTokenInput = document.getRequiredHtmlElementById("emailToken");
            const verifyLoadingButton = LoadingButton.getOrCreateInstance(verifyButton);
            const continueLoadingButton = LoadingButton.getOrCreateInstance(continueButton);
            // Reset to step 1
            domainInputStep.bootstrapShow();
            verificationMethodsStep.bootstrapHide();
            emailTokenStep.bootstrapHide();
            verifyLoadingButton.stop();
            verifyButton.bootstrapHide();
            continueLoadingButton.stop();
            continueButton.bootstrapShow();
            // Make sure the Go Back button is hidden in step 1
            goBackButton.bootstrapHide();
            goBackButton.dataset.bsDismiss = "modal"; // Reset to allow modal dismissal
            domainInput.value = "";
            domainInput.classList.remove("is-invalid");
            emailTokenInput.value = "";
            emailTokenInput.classList.remove("is-invalid");
            // Reset the verification flow
            tabContent.bootstrapShow();
            // Reset tab to the first tab (Email)
            document.whenHtmlElementExists("email-tab", (emailTab) => {
                // Create a new Bootstrap Tab instance and show it
                const bootstrapTab = new Tab(emailTab);
                bootstrapTab.show();
            });
        });
    });
    // Add global modal cleanup handler for domain removal
    document.addEventListener("hidden.bs.modal", (event) => {
        if (event.target.id === "domainRemovalPrompt") {
            // Force cleanup of any lingering modal backdrops
            const modalBackdrops = document.queryHtmlElements(".modal-backdrop");
            for (const backdrop of modalBackdrops) {
                backdrop.classList.remove("show");
                backdrop.classList.remove("fade");
                backdrop.remove();
            }
            // Restore body styling
            document.body.classList.remove("modal-open");
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        }
    });
}
